/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import type {
  DynamicSelectEditorDescription_OutputSource,
  Editor,
  InputDescription,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import {
  DynamicSelectEditorDescription_InputType,
  DynamicSelectEditorDescription_OutputSource as OutputSource,
  ValueDescription_T,
} from "@smartsuite/automation-engine-api";
import {
  DropdownPanel,
  type FieldTypePillProps,
  MenuGroupContent,
  MenuLineSeparator,
  MenuOption,
  MenuOptionWrapper,
  Paragraph,
  PickerInputField,
  Text,
} from "@smartsuite/react-ui/lib";
import { KeyCode } from "@smartsuite/react-ui/lib/constants/keycode";
import { observer } from "mobx-react-lite";
import type { MutableRefObject, Ref } from "react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as R from "remeda";
import { SolutionColorVariables } from "@smartsuite/types";
import { CaptionBlock } from "../../../../../components/CaptionBlock";
import { useFieldMissingValidation } from "../../../../../stores/automation/hooks/field-missing-validation";
import { checkInputValidation } from "../../../../../stores/automation/utils/inputValidation";
import { useRootStore } from "../../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../types/candidate";
import type {
  DynamicSection,
  DynamicSectionOption,
} from "../../../../../types/line";
import {
  ActionLabel,
  MENU_MAX_HEIGHT,
  MENU_MAX_HEIGHT_MARGIN,
} from "../../../../../utils/constants";
import { getLocalCssColorVariables } from "../../../../../utils/cssVariablesUtils";
import { FieldMissingError } from "../../../Sections/ErrorSection";
import { CurrentOption } from "../FieldEditor/FieldEditorRow/containers/CurrentOption";
import { UpdateValueDropdown } from "../FieldEditor/FieldEditorRow/containers/UpdateValueDropdown";
import { ifLoopingSourceIsActionFind } from "../FieldEditor/FieldEditorRow/fields/utils";
import { useLoopingMenu } from "../hooks/use-looping-menu";
import { POSITION_AT, POSITION_MY } from "../style";
import {
  convertActionsToDynamicSections,
  convertTriggerToDynamicSection,
} from "../utils";
import { currentItemOutputsRefPill } from "../utils/current-item-outputs-ref-pill";
import { Root, StyledNoFields, StyledReactInputFieldContainer } from "./style";
import {
  ejectPillFromInput,
  handleActionOutput,
  handleTriggerOutput,
  triggerToOptions,
  useConverters,
} from "../EmailEditor/utils";
import { generalTypes, ssTypes } from "./string-output-types";
import type { ActionOptionGroup } from "../utils/action-options";
import { actionsToOptions } from "../utils/action-options";
import type { LoopScope } from "../../../../../stores/automation/looping.store";

interface TriggerActionOption {
  title: string;
  outputId: string;
  icon?: string;
  hash?: string;
  contextSpecific?: boolean;
}
interface Pill extends FieldTypePillProps {
  type:
    | "actionOutput"
    | "triggerOutput"
    | "member"
    | "team"
    | "custom"
    | "loop";
  value: string;
}

type DynamicSelectTab =
  | "root"
  | "fields"
  | "actions"
  | "options"
  | "current"
  | "sub-menu";
type DynamicSelectOutputTab = "fields" | "actions" | "current";

const getMenuHeight = (windowHeight: number): number => {
  if (windowHeight < MENU_MAX_HEIGHT * 2 + MENU_MAX_HEIGHT_MARGIN) {
    return windowHeight / 2 - MENU_MAX_HEIGHT_MARGIN;
  }

  return MENU_MAX_HEIGHT;
};

const getGroupedDynamicSectionOptions = (
  options: DynamicSectionOption[],
  selectedOutputIds: Set<string>,
  searchQuery: string,
): DynamicSectionOption[] => {
  const query = searchQuery.toLowerCase();
  const filteredOptions = options
    .filter((option) => !selectedOutputIds.has(option.outputId))
    .filter((option) => {
      if (query === "") {
        return true;
      }

      const label = option.label.toLowerCase();
      const labelSuffix = option.labelSuffix?.toLowerCase() ?? "";
      return label.includes(query) || labelSuffix.includes(query);
    })
    .sort((left, right) => left.label.localeCompare(right.label));

  const groupedOptions: DynamicSectionOption[] = [];
  filteredOptions.forEach((option) => {
    if (option.groupFieldId !== undefined) {
      const group = groupedOptions.find(
        (groupedOption) => groupedOption.groupFieldId === option.groupFieldId,
      );

      if (group === undefined) {
        groupedOptions.push({
          ...option,
          nestedOptions: [],
          label: option.label,
        });
        return;
      }

      group.nestedOptions = [...(group.nestedOptions ?? []), option];
      return;
    }

    groupedOptions.push({
      ...option,
      nestedOptions: [],
      groupFieldId: "",
      label: option.label,
    });
  });

  return groupedOptions;
};

const getDynamicOptionLabel = (option: DynamicSectionOption): string =>
  option.labelSuffix && !option.nestedOptions?.length
    ? `${option.label}: ${option.labelSuffix}`
    : option.label;

const flattenDynamicSectionOptionsToTriggerOptions = (
  options: DynamicSectionOption[],
): TriggerActionOption[] =>
  options.flatMap((option) => [
    {
      title: getDynamicOptionLabel(option),
      outputId: option.outputId,
      icon: option.icon,
      contextSpecific: option.contextSpecific,
    },
    ...(option.nestedOptions
      ? flattenDynamicSectionOptionsToTriggerOptions(option.nestedOptions)
      : []),
  ]);

type DynamicSelectPill =
  | {
      type: "triggerOutput";
      triggerReference: TriggerCandidate["triggerReference"];
      outputId: string;
    }
  | {
      type: "actionOutput";
      actionReference: NonNullable<ActionCandidate["actionReference"]>;
      outputId: string;
    }
  | {
      type: "custom";
      value: string;
    }
  | {
      type: "loop";
      looping: LoopScope;
    };

const mergeNextDynamicSelectPillToInput = (
  input: InputCandidate,
  pill: DynamicSelectPill,
  multiple: boolean,
): InputCandidate => ({
  ...input,
  value: {
    values: [
      ...(multiple && R.isTruthy(input.value) ? input.value.values : []),
      ...(() => {
        switch (pill.type) {
          case "triggerOutput":
            if (!pill.triggerReference) {
              return [];
            }
            return [
              {
                value: {
                  oneofKind: "reference",
                  reference: {
                    source: {
                      oneofKind: "triggerReference",
                      triggerReference: pill.triggerReference,
                    },
                    outputId: pill.outputId,
                  },
                } as const,
              },
            ];
          case "actionOutput":
            return [
              {
                value: {
                  oneofKind: "reference",
                  reference: {
                    source: {
                      oneofKind: "actionReference",
                      actionReference: pill.actionReference,
                    },
                    outputId: pill.outputId,
                  },
                } as const,
              },
            ];
          case "custom":
            return [
              {
                value: {
                  oneofKind: "string",
                  string: pill.value,
                } as const,
              },
            ];
          case "loop":
            return currentItemOutputsRefPill(pill.looping);
          default:
            return [];
        }
      })(),
    ],
  },
});

export const DynamicSelectEditor = observer<{
  description?: InputDescription;
  editor: Editor;
  config: InputCandidate;
  trigger: TriggerCandidate;
  onChange: (input: InputCandidate) => void;
  showValidationError: boolean;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  integrations: IntegrationDescription[];
  isMultiple: boolean;
  isError?: boolean;
  onClickTrash?: () => void;
}>(
  ({
    description,
    editor,
    config,
    trigger,
    onChange,
    showValidationError,
    actions,
    action,
    integrations,
    isMultiple,
    isError,
    onClickTrash,
  }) => {
    const { t } = useTranslation();
    const store = useRootStore();
    const [isOpen, setIsOpen] = useState(false);
    const [tab, setTab] = useState<DynamicSelectTab>("options");
    const [triggerFields, setTriggerFields] = useState<TriggerActionOption[]>(
      [],
    );
    const [actionFields, setActionFields] = useState<ActionOptionGroup[]>([]);
    const [triggerMenu, setTriggerMenu] = useState<DynamicSection | null>(null);
    const [actionMenus, setActionMenus] = useState<DynamicSection[]>([]);
    const [selectedAction, setSelectedAction] = useState<
      ActionOptionGroup | undefined
    >();
    const [selectedActionMenu, setSelectedActionMenu] = useState<
      DynamicSection | undefined
    >();
    const [subMenuOptions, setSubMenuOptions] = useState<
      DynamicSectionOption[]
    >([]);
    const [subMenuTitle, setSubMenuTitle] = useState("");
    const [prevTab, setPrevTab] = useState<DynamicSelectOutputTab>("fields");
    const [searchQuery, setSearchQuery] = useState("");
    const [validationError, setValidationError] = useState("");
    const [focus, setFocus] = useState(false);
    const [menuHeight, setMenuHeight] = useState(MENU_MAX_HEIGHT);
    const [isCleanAction, setIsCleanAction] = useState(false);
    const [actionType, setActionType] = useState<
      | "replace"
      | "clear"
      | "append"
      | "mathAdd"
      | "mathSubtract"
      | "mathMultiply"
      | "mathDivide"
      | undefined
    >(config.operatorConfig?.operator?.oneofKind);
    const buttonRef = useRef<HTMLButtonElement>();
    const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
      useFieldMissingValidation();
    const editorWrapperId = `${config.inputId}-editor-wrapper`;
    const rootLooping = store.automations.candidate.looping;
    const looping = action
      ? (rootLooping?.scopeForAction?.(action.hash) ?? rootLooping)
      : rootLooping;
    const { convertInputToPills } = useConverters();

    const dynamicEditor =
      description?.editor?.editor.oneofKind === "dynamicSelect"
        ? description.editor.editor.dynamicSelect
        : editor.editor.oneofKind === "dynamicSelect"
          ? editor.editor.dynamicSelect
          : undefined;

    const optionLabel = dynamicEditor?.optionLabel ?? "Options";
    const staticOptions = dynamicEditor?.options ?? [];
    const outputsSource = dynamicEditor?.outputsSource ?? [];
    const allowManual =
      dynamicEditor?.manual !==
      DynamicSelectEditorDescription_InputType.NON_EDITABLE;
    const showTriggerOutputs =
      outputsSource.length === 0 ||
      outputsSource.includes(
        OutputSource.TRIGGER as DynamicSelectEditorDescription_OutputSource,
      );
    const showActionOutputs =
      outputsSource.length === 0 ||
      outputsSource.includes(
        OutputSource.ACTIONS as DynamicSelectEditorDescription_OutputSource,
      );

    const { loopingFields, loopingMenu } = useLoopingMenu({
      trigger,
      actions,
      integrations,
      types: [ValueDescription_T.STRING],
      ssFieldTypes: [],
      systemAllowedOptions: [],
      action,
    });

    useEffect(() => {
      const handleResize = (): void => {
        setMenuHeight(getMenuHeight(window.innerHeight));
      };

      handleResize();
      window.addEventListener("resize", handleResize, false);
      return () => window.removeEventListener("resize", handleResize, false);
    }, []);

    useEffect(() => {
      if (showValidationError && R.isTruthy(description)) {
        setValidationError(checkInputValidation(description, config ?? []));
      } else {
        setValidationError("");
      }
    }, [config, description, showValidationError]);

    useEffect(() => {
      if (!showTriggerOutputs) {
        setTriggerFields([]);
        setTriggerMenu(null);
        return;
      }

      const nextTriggerMenu = convertTriggerToDynamicSection(
        trigger,
        integrations,
        generalTypes,
        generalTypes,
        ssTypes,
      );

      setTriggerMenu(nextTriggerMenu);
      setTriggerFields(
        nextTriggerMenu === null
          ? triggerToOptions(trigger, generalTypes, generalTypes, ssTypes)
          : flattenDynamicSectionOptionsToTriggerOptions(
              nextTriggerMenu.options,
            ),
      );
    }, [integrations, showTriggerOutputs, trigger]);

    useEffect(() => {
      if (!showActionOutputs || action === undefined) {
        setActionFields([]);
        setActionMenus([]);
        return;
      }

      setActionFields(
        actionsToOptions(
          actions,
          action,
          integrations,
          [ValueDescription_T.STRING],
          [],
          [],
          looping,
        ),
      );
      setActionMenus(
        convertActionsToDynamicSections(
          actions,
          action,
          integrations,
          [ValueDescription_T.STRING],
          [],
          [],
          looping,
        ),
      );
    }, [actions, action, integrations, looping, showActionOutputs]);

    useEffect(() => {
      if (store.inputToFocus?.inputId === config.inputId) {
        setIsOpen(true);
        const focusTimeoutId = setTimeout(() => {
          const wrapper = document.getElementById(editorWrapperId);
          const inputElement = wrapper?.querySelector(
            ".picker-input-field__input",
          );
          // @ts-expect-error focusable UI input
          inputElement?.focus();
          store.inputToFocus = undefined;
        });
        return () => clearTimeout(focusTimeoutId);
      }
    }, []);

    useEffect(() => {
      const hasOptions = staticOptions.length > 0;
      if (hasOptions) {
        setTab("options");
      } else if ((triggerMenu?.options.length ?? 0) > 0) {
        setTab("fields");
      } else if (actionMenus.some((group) => group.options.length > 0)) {
        setTab("actions");
      } else {
        setTab("root");
      }
    }, [actionMenus, staticOptions.length, triggerMenu?.options.length]);

    useEffect(() => {
      if (actionType === undefined) {
        return;
      }
      if (actionType === "clear") {
        setIsOpen(false);
        setIsCleanAction(true);
      } else {
        setIsCleanAction(false);
      }
      onChange({
        ...config,
        value:
          actionType === "clear"
            ? {
                values: [],
              }
            : config.value,
        operatorConfig: {
          // @ts-expect-error proto union
          operator: {
            oneofKind: actionType,
            [actionType]: {},
          },
        },
      });
    }, [actionType, isOpen]);

    const pillList = useMemo(() => {
      const basePills = convertInputToPills(
        config,
        [],
        [],
        triggerFields,
        actionFields,
        trigger,
        actions,
        action,
        loopingFields,
      );

      return basePills.map((pill) => {
        if (pill.type !== "custom") {
          return pill;
        }
        const option = staticOptions.find((item) => item.value === pill.value);
        return option
          ? {
              ...pill,
              title: option.label,
            }
          : pill;
      });
    }, [
      action,
      actionFields,
      actions,
      config,
      convertInputToPills,
      loopingFields,
      staticOptions,
      trigger,
      triggerFields,
    ]);

    const isNextPillAllowed = (
      currentConfig: InputCandidate,
      multiple: boolean,
    ): boolean => {
      if (!multiple && currentConfig.value?.values.length === 1) {
        if (focus) {
          setFocus(false);
        }
        return false;
      }
      return true;
    };

    const addCustomValue = (value: string): void => {
      if (value.trim() === "") {
        return;
      }
      setIsOpen(false);
      onChange(
        mergeNextDynamicSelectPillToInput(
          config,
          {
            type: "custom",
            value,
          },
          isNextPillAllowed(config, isMultiple),
        ),
      );
    };

    const isFieldError = (pills: Pill[]): boolean =>
      Boolean(pills.find((item) => item.isError));

    const availableOptions = staticOptions
      .filter(
        (option) =>
          !pillList.some(
            (pill) => pill.type === "custom" && pill.value === option.value,
          ),
      )
      .filter((option) => {
        const query = searchQuery.toLowerCase();
        return (
          option.label.toLowerCase().includes(query) ||
          option.value.toLowerCase().includes(query)
        );
      });

    const unselectedTriggerFields = triggerFields
      .filter(
        (option) =>
          !pillList.some(
            (pill) =>
              pill.type === "triggerOutput" && pill.value === option.outputId,
          ),
      )
      .filter((option) =>
        option.title.toLowerCase().includes(searchQuery.toLowerCase()),
      );

    const selectedTriggerOutputIds = new Set(
      pillList
        .filter((pill) => pill.type === "triggerOutput")
        .map((pill) => pill.value),
    );
    const selectedActionOutputIds = new Set(
      pillList
        .filter((pill) => pill.type === "actionOutput")
        .map((pill) => pill.value),
    );
    const groupedTriggerFields = getGroupedDynamicSectionOptions(
      triggerMenu?.options ?? [],
      selectedTriggerOutputIds,
      searchQuery,
    );
    const groupedCurrentFields = getGroupedDynamicSectionOptions(
      loopingMenu?.options ?? [],
      selectedActionOutputIds,
      searchQuery,
    );
    const groupedActionFields = getGroupedDynamicSectionOptions(
      selectedActionMenu?.options ?? [],
      selectedActionOutputIds,
      searchQuery,
    );

    const openSubMenu = (
      options: DynamicSectionOption[],
      label: string,
      sourceTab: DynamicSelectOutputTab,
    ): void => {
      setSubMenuOptions(options);
      setSubMenuTitle(label);
      setPrevTab(sourceTab);
      setTab("sub-menu");
      setSearchQuery("");
    };

    const changeActionType = (
      type:
        | "replace"
        | "clear"
        | "append"
        | "mathAdd"
        | "mathSubtract"
        | "mathMultiply"
        | "mathDivide"
        | undefined,
    ): void => {
      setActionType(type);
    };

    const hasError =
      Boolean(isError) ||
      isFieldError(pillList) ||
      validationError !== "" ||
      !isInputValidByFieldMissing(config, action);

    const classNames = [
      isError ? "is-error" : "",
      hasError ? "pill-picker-error" : "",
      isCleanAction ? "is-clear" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <Root>
        <StyledReactInputFieldContainer id={editorWrapperId}>
          {description !== undefined && (
            <CaptionBlock
              description={description}
              onClickTrash={onClickTrash}
            />
          )}
          <PickerInputField
            showAddButton={!isCleanAction}
            className={classNames}
            onChangeFocused={(isFocused, event) => {
              setFocus(isFocused);

              if (
                description?.editor?.hidePopupOnHover !== true &&
                isFocused &&
                event?.target.className !== "field-type-pill__remove" &&
                event?.target.className !==
                  "icon-button icon-button--kind-circle input-picker-group__plus-button"
              ) {
                setIsOpen(true);
              }
            }}
            onInputChange={() => {
              setIsOpen(false);
            }}
            state={hasError ? "error" : isOpen || focus ? "active" : "default"}
            // @ts-expect-error react-ui button ref type
            buttonRef={buttonRef as Ref<HTMLButtonElement>}
            isOpen={isOpen}
            pills={pillList}
            pillStyle={{
              textSize: "m",
            }}
            onButtonClick={() => setIsOpen((open) => !open)}
            onPillRemove={(index) => {
              setIsOpen(false);
              onChange(ejectPillFromInput(config, index));
            }}
            allowManualInput={
              !isCleanAction &&
              allowManual &&
              isNextPillAllowed(config, isMultiple)
            }
            onInputAdd={addCustomValue}
            placeholder={
              isCleanAction
                ? t("automation-form.clean-operator-editor-placeholder")
                : isOpen || focus
                  ? pillList.length > 0
                    ? ""
                    : allowManual
                      ? t("editor.type-or-use-field")
                      : t("editor.use-field")
                  : ""
            }
            keyDownCodes={[KeyCode.tab, KeyCode.enter, KeyCode.space]}
          >
            {action?.actionReference?.actionId === ActionLabel.updateRecord &&
              description?.operators?.operators !== undefined &&
              description.operators.operators.length > 0 && (
                <UpdateValueDropdown
                  operatorConfig={config.operatorConfig}
                  operatorDescriptions={description.operators.operators}
                  onOpenedChange={setFocus}
                  onChange={changeActionType}
                />
              )}

            {isOpen && (
              <DropdownPanel
                targetElementRef={buttonRef as MutableRefObject<HTMLElement>}
                clickOutsideExcept={[".dropdown-panel"]}
                isActive={isOpen}
                at={POSITION_AT}
                my={POSITION_MY}
                minWidth={300}
                arrowBackground="custom"
                arrowCustomBackground={SolutionColorVariables.sc1}
                panelLineBackground={SolutionColorVariables.sc1}
                portalStyles={getLocalCssColorVariables(store.theme)}
                onClickOutside={() => setIsOpen(false)}
                onCloseAnimationEnd={() => {
                  setSearchQuery("");
                  setIsOpen(false);
                }}
              >
                {tab === "root" && (
                  <MenuOptionWrapper>
                    {availableOptions.length > 0 && (
                      <MenuOption
                        submenuArrow="right"
                        onClick={() => setTab("options")}
                      >
                        {optionLabel}
                      </MenuOption>
                    )}
                    {unselectedTriggerFields.length > 0 && (
                      <MenuOption
                        submenuArrow="right"
                        onClick={() => setTab("fields")}
                      >
                        {t("automation-form.fields-from-trigger")}
                      </MenuOption>
                    )}
                    {actionMenus.map(
                      (actionMenu) =>
                        actionMenu?.options.length > 0 && (
                          <MenuOption
                            key={actionMenu.name}
                            submenuArrow="right"
                            onClick={() => {
                              setSelectedAction(
                                actionFields.find(
                                  (optionGroup) =>
                                    optionGroup.name === actionMenu.name,
                                ),
                              );
                              setSelectedActionMenu(actionMenu);
                              setTab("actions");
                              setSearchQuery("");
                            }}
                          >
                            {actionMenu.hideNamePrefix === true
                              ? actionMenu.name
                              : t("field-popups.action-popup", {
                                  name: actionMenu.name,
                                })}
                          </MenuOption>
                        ),
                    )}
                    <CurrentOption
                      loopingMenu={loopingFields}
                      addPill={() => {
                        if (
                          ifLoopingSourceIsActionFind(looping) ||
                          loopingMenu?.options?.length
                        ) {
                          setTab("current");
                          return;
                        }

                        if (looping.source) {
                          onChange(
                            mergeNextDynamicSelectPillToInput(
                              config,
                              {
                                type: "loop",
                                looping,
                              },
                              isNextPillAllowed(config, isMultiple),
                            ),
                          );
                        }
                      }}
                    />
                  </MenuOptionWrapper>
                )}

                {tab === "options" && (
                  <MenuGroupContent
                    title={optionLabel}
                    onSearchChange={(query) => setSearchQuery(query)}
                    onNavigateBack={() => {
                      setTab("root");
                      setSearchQuery("");
                    }}
                    maxHeight={menuHeight}
                  >
                    {availableOptions.map((option, index) => (
                      <Fragment key={option.value}>
                        {option.horizontalSeparator === true && index > 0 && (
                          <MenuLineSeparator />
                        )}
                        <MenuOption
                          key={option.value}
                          iconName={option.icon}
                          iconSize="S"
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            onChange(
                              mergeNextDynamicSelectPillToInput(
                                config,
                                {
                                  type: "custom",
                                  value: option.value,
                                },
                                isNextPillAllowed(config, isMultiple),
                              ),
                            );
                          }}
                        >
                          {option.label}
                        </MenuOption>
                      </Fragment>
                    ))}
                    {availableOptions.length < 1 && (
                      <StyledNoFields>No results</StyledNoFields>
                    )}
                  </MenuGroupContent>
                )}

                {tab === "fields" && (
                  <MenuGroupContent
                    searchPlaceholder={t("automation-form.search-fields")}
                    maxHeight={menuHeight}
                    title={t("automation-form.fields-from-trigger")}
                    onSearchChange={(query) => setSearchQuery(query)}
                    onNavigateBack={() => {
                      setTab("root");
                      setSearchQuery("");
                    }}
                  >
                    {groupedTriggerFields
                      .filter((option) => !option.contextSpecific)
                      .map((option) => (
                        <MenuOption
                          key={option.outputId}
                          submenuArrow={
                            option.nestedOptions?.length ? "right" : undefined
                          }
                          onClick={() => {
                            if (option.nestedOptions?.length) {
                              openSubMenu(
                                [option, ...option.nestedOptions],
                                option.label,
                                "fields",
                              );
                              return;
                            }

                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            if (R.isTruthy(trigger.triggerReference)) {
                              onChange(
                                mergeNextDynamicSelectPillToInput(
                                  config,
                                  {
                                    type: "triggerOutput",
                                    triggerReference: trigger.triggerReference,
                                    outputId: option.outputId,
                                  },
                                  isNextPillAllowed(config, isMultiple),
                                ),
                              );
                            }
                          }}
                          iconName={option.icon}
                          iconSize="S"
                        >
                          {getDynamicOptionLabel(option)}
                        </MenuOption>
                      ))}

                    {groupedTriggerFields.filter(
                      (option) => !option.contextSpecific,
                    ).length > 0 &&
                      groupedTriggerFields.filter(
                        (option) => option.contextSpecific,
                      ).length > 0 && <MenuLineSeparator />}

                    {groupedTriggerFields
                      .filter((option) => option.contextSpecific)
                      .map((option) => (
                        <MenuOption
                          key={option.outputId}
                          submenuArrow={
                            option.nestedOptions?.length ? "right" : undefined
                          }
                          onClick={() => {
                            if (option.nestedOptions?.length) {
                              openSubMenu(
                                [option, ...option.nestedOptions],
                                option.label,
                                "fields",
                              );
                              return;
                            }

                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            if (R.isTruthy(trigger.triggerReference)) {
                              onChange(
                                mergeNextDynamicSelectPillToInput(
                                  config,
                                  {
                                    type: "triggerOutput",
                                    triggerReference: trigger.triggerReference,
                                    outputId: option.outputId,
                                  },
                                  isNextPillAllowed(config, isMultiple),
                                ),
                              );
                            }
                          }}
                          iconName={option.icon}
                          iconSize="S"
                        >
                          {getDynamicOptionLabel(option)}
                        </MenuOption>
                      ))}

                    {groupedTriggerFields.length < 1 && (
                      <StyledNoFields>No results</StyledNoFields>
                    )}
                  </MenuGroupContent>
                )}

                {tab === "current" && loopingMenu !== undefined && (
                  <MenuGroupContent
                    title={t("general.current-item")}
                    maxHeight={menuHeight}
                    onSearchChange={(query) => setSearchQuery(query)}
                    onNavigateBack={() => {
                      setTab("root");
                      setSearchQuery("");
                    }}
                  >
                    {groupedCurrentFields
                      .filter((option) => !option.contextSpecific)
                      .map((option) => (
                        <MenuOption
                          key={option.outputId}
                          submenuArrow={
                            option.nestedOptions?.length ? "right" : undefined
                          }
                          onClick={() => {
                            if (option.nestedOptions?.length) {
                              openSubMenu(
                                [option, ...option.nestedOptions],
                                option.label,
                                "current",
                              );
                              return;
                            }

                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });
                            const actionConfig = handleActionOutput(looping, {
                              outputId: option.outputId,
                            });
                            if (actionConfig) {
                              onChange(
                                mergeNextDynamicSelectPillToInput(
                                  config,
                                  actionConfig,
                                  isNextPillAllowed(config, isMultiple),
                                ),
                              );
                              return;
                            }

                            const triggerConfig = handleTriggerOutput(trigger, {
                              outputId: option.outputId,
                            });
                            if (triggerConfig) {
                              onChange(
                                mergeNextDynamicSelectPillToInput(
                                  config,
                                  triggerConfig,
                                  isNextPillAllowed(config, isMultiple),
                                ),
                              );
                            }
                          }}
                          iconName={option.icon}
                          iconSize="S"
                        >
                          <Text styles={{ marginLeft: "13px" }}>
                            {getDynamicOptionLabel(option)}
                          </Text>
                        </MenuOption>
                      ))}
                    {groupedCurrentFields.length < 1 && (
                      <StyledNoFields>No results</StyledNoFields>
                    )}
                  </MenuGroupContent>
                )}

                {tab === "actions" &&
                  selectedAction !== undefined &&
                  selectedActionMenu !== undefined && (
                    <MenuGroupContent
                      title={
                        selectedActionMenu.hideNamePrefix === true
                          ? selectedActionMenu.name
                          : t("field-popups.action-popup", {
                              name: selectedActionMenu.name,
                            })
                      }
                      maxHeight={menuHeight}
                      onSearchChange={(query) => setSearchQuery(query)}
                      onNavigateBack={() => {
                        setTab("root");
                        setSearchQuery("");
                      }}
                    >
                      {groupedActionFields
                        .filter((option) => !option.contextSpecific)
                        .map((option) => (
                          <MenuOption
                            key={option.outputId}
                            submenuArrow={
                              option.nestedOptions?.length ? "right" : undefined
                            }
                            onClick={() => {
                              if (option.nestedOptions?.length) {
                                openSubMenu(
                                  [option, ...option.nestedOptions],
                                  option.label,
                                  "actions",
                                );
                                return;
                              }

                              setTimeout(() => {
                                setIsOpen(isMultiple);
                              });
                              if (
                                R.isTruthy(
                                  selectedAction.action.actionReference,
                                )
                              ) {
                                onChange(
                                  mergeNextDynamicSelectPillToInput(
                                    config,
                                    {
                                      type: "actionOutput",
                                      actionReference:
                                        selectedAction.action.actionReference,
                                      outputId: option.outputId,
                                    },
                                    isNextPillAllowed(config, isMultiple),
                                  ),
                                );
                              }
                            }}
                            iconName={option.icon}
                            iconSize="S"
                          >
                            <Text styles={{ marginLeft: "13px" }}>
                              {getDynamicOptionLabel(option)}
                            </Text>
                          </MenuOption>
                        ))}

                      {groupedActionFields.filter(
                        (option) => !option.contextSpecific,
                      ).length > 0 &&
                        groupedActionFields.filter(
                          (option) => option.contextSpecific,
                        ).length > 0 && <MenuLineSeparator />}

                      {groupedActionFields
                        .filter((option) => option.contextSpecific)
                        .map((option) => (
                          <MenuOption
                            key={option.outputId}
                            submenuArrow={
                              option.nestedOptions?.length ? "right" : undefined
                            }
                            onClick={() => {
                              if (option.nestedOptions?.length) {
                                openSubMenu(
                                  [option, ...option.nestedOptions],
                                  option.label,
                                  "actions",
                                );
                                return;
                              }

                              setTimeout(() => {
                                setIsOpen(isMultiple);
                              });
                              if (
                                R.isTruthy(
                                  selectedAction.action.actionReference,
                                )
                              ) {
                                onChange(
                                  mergeNextDynamicSelectPillToInput(
                                    config,
                                    {
                                      type: "actionOutput",
                                      actionReference:
                                        selectedAction.action.actionReference,
                                      outputId: option.outputId,
                                    },
                                    isNextPillAllowed(config, isMultiple),
                                  ),
                                );
                              }
                            }}
                            iconName={option.icon}
                            iconSize="S"
                          >
                            <Text styles={{ marginLeft: "13px" }}>
                              {getDynamicOptionLabel(option)}
                            </Text>
                          </MenuOption>
                        ))}
                      {groupedActionFields.length < 1 && (
                        <StyledNoFields>No results</StyledNoFields>
                      )}
                    </MenuGroupContent>
                  )}

                {tab === "sub-menu" && (
                  <MenuGroupContent
                    title={subMenuTitle}
                    searchPlaceholder={t("automation-form.search-fields")}
                    maxHeight={menuHeight}
                    onSearchChange={(query) => setSearchQuery(query)}
                    onNavigateBack={() => {
                      setTab(prevTab);
                      setSearchQuery("");
                    }}
                  >
                    {subMenuOptions
                      .filter(
                        (option) =>
                          option.outputId !== subMenuOptions[0]?.outputId,
                      )
                      .filter((option) => {
                        const selectedIds =
                          prevTab === "fields"
                            ? selectedTriggerOutputIds
                            : selectedActionOutputIds;
                        return !selectedIds.has(option.outputId);
                      })
                      .filter((option) => {
                        const query = searchQuery.toLowerCase();
                        if (query === "") {
                          return true;
                        }
                        return (
                          option.label.toLowerCase().includes(query) ||
                          (option.labelSuffix?.toLowerCase() ?? "").includes(
                            query,
                          )
                        );
                      })
                      .map((option) => (
                        <MenuOption
                          key={option.outputId}
                          iconName={option.icon}
                          iconSize="S"
                          onClick={() => {
                            setTimeout(() => {
                              setIsOpen(isMultiple);
                            });

                            if (
                              prevTab === "fields" &&
                              R.isTruthy(trigger.triggerReference)
                            ) {
                              onChange(
                                mergeNextDynamicSelectPillToInput(
                                  config,
                                  {
                                    type: "triggerOutput",
                                    triggerReference: trigger.triggerReference,
                                    outputId: option.outputId,
                                  },
                                  isNextPillAllowed(config, isMultiple),
                                ),
                              );
                              return;
                            }

                            if (
                              prevTab === "actions" &&
                              R.isTruthy(selectedAction?.action.actionReference)
                            ) {
                              onChange(
                                mergeNextDynamicSelectPillToInput(
                                  config,
                                  {
                                    type: "actionOutput",
                                    actionReference:
                                      selectedAction.action.actionReference,
                                    outputId: option.outputId,
                                  },
                                  isNextPillAllowed(config, isMultiple),
                                ),
                              );
                              return;
                            }

                            if (prevTab === "current") {
                              const actionConfig = handleActionOutput(looping, {
                                outputId: option.outputId,
                              });
                              if (actionConfig) {
                                onChange(
                                  mergeNextDynamicSelectPillToInput(
                                    config,
                                    actionConfig,
                                    isNextPillAllowed(config, isMultiple),
                                  ),
                                );
                                return;
                              }

                              const triggerConfig = handleTriggerOutput(
                                trigger,
                                {
                                  outputId: option.outputId,
                                },
                              );
                              if (triggerConfig) {
                                onChange(
                                  mergeNextDynamicSelectPillToInput(
                                    config,
                                    triggerConfig,
                                    isNextPillAllowed(config, isMultiple),
                                  ),
                                );
                              }
                            }
                          }}
                        >
                          {getDynamicOptionLabel(option)}
                        </MenuOption>
                      ))}
                    {subMenuOptions.filter(
                      (option) =>
                        option.outputId !== subMenuOptions[0]?.outputId,
                    ).length < 1 && <StyledNoFields>No results</StyledNoFields>}
                  </MenuGroupContent>
                )}
              </DropdownPanel>
            )}
          </PickerInputField>
          {validationError !== "" && (
            <Paragraph color={"error"} size={"xs"}>
              {validationError}
            </Paragraph>
          )}
          <FieldMissingError
            message={getFieldMissingErrorMessage(config, action)}
          />
        </StyledReactInputFieldContainer>
      </Root>
    );
  },
);

DynamicSelectEditor.displayName = "DynamicSelectEditor";
